/**
 * Imperative Rule Evaluation Helper
 * Phase D Step 7: On-demand rule checks at action time
 * 
 * Unlike the useRuleEvaluation hook (reactive), this runs once
 * and returns a Promise — ideal for intercepting form submissions.
 */

import { supabase } from '@/integrations/supabase/client';
import { getRuleByCode, getRulesByTarget, type RuleDefinition } from './ruleDefinitions';
import type { RuleActionContext, RuleResult } from './useRuleEngine';
import { logger } from '@/lib/logger';

interface EvaluationResult {
  results: RuleResult[];
  violations: RuleResult[];
  passed: RuleResult[];
  canProceed: boolean;
}

/**
 * Evaluate all enabled rules for a given action context.
 * Fetches enabled rules from DB, filters by target module, evaluates each.
 */
export async function evaluateRulesImperative(
  projectId: string,
  context: RuleActionContext
): Promise<EvaluationResult> {
  try {
    // 1. Fetch enabled rules with overrides
    const { data: dbRules, error } = await supabase
      .from('project_rules' as any)
      .select('rule_code, enabled, custom_severity, custom_condition_value')
      .eq('project_id', projectId);

    if (error) {
      logger.error('[evaluateRulesImperative] Failed to fetch rules:', error);
      return { results: [], violations: [], passed: [], canProceed: true };
    }

    // 2. Get all rule definitions for this target module
    const targetRules = getRulesByTarget(context.targetModule);

    // 3. Filter to enabled rules (default ON if no DB row)
    const enabledRules = targetRules.filter(def => {
      const dbRow = (dbRules as any[] || []).find((r: any) => r.rule_code === def.code);
      return dbRow ? dbRow.enabled : true;
    });

    // 4. Build overrides map
    const overridesMap = new Map<string, { severity?: string; conditionValue?: string }>();
    (dbRules as any[] || []).forEach((r: any) => {
      if (r.custom_severity || r.custom_condition_value) {
        overridesMap.set(r.rule_code, {
          severity: r.custom_severity || undefined,
          conditionValue: r.custom_condition_value || undefined,
        });
      }
    });

    // 5. Evaluate each default rule
    const results: RuleResult[] = [];
    for (const def of enabledRules) {
      const overrides = overridesMap.get(def.code);
      const result = await evaluateRule(def, projectId, context, overrides);
      results.push(result);
    }

    // 6. Evaluate custom rules
    const { data: customRules } = await supabase
      .from('custom_rules' as any)
      .select('rule_code, name, description, target_module, source_module, condition_type, condition_value, severity')
      .eq('project_id', projectId)
      .eq('enabled', true)
      .eq('target_module', context.targetModule);

    if (customRules && (customRules as any[]).length > 0) {
      for (const cr of customRules as any[]) {
        const result = await evaluateCustomRule(cr, projectId);
        results.push(result);
      }
    }

    const violations = results.filter(r => !r.passed);
    const passed = results.filter(r => r.passed);
    const canProceed = violations.every(v => v.severity === 'warn' || v.severity === 'info');

    return { results, violations, passed, canProceed };
  } catch (error) {
    logger.error('[evaluateRulesImperative] Unexpected error:', error);
    return { results: [], violations: [], passed: [], canProceed: true };
  }
}

// Re-implement evaluation logic (mirrors useRuleEngine.ts but standalone)
async function evaluateRule(
  def: RuleDefinition,
  projectId: string,
  context: RuleActionContext,
  overrides?: { severity?: string; conditionValue?: string }
): Promise<RuleResult> {
  const effectiveSeverity = (overrides?.severity || def.severity) as 'block' | 'warn' | 'info';
  const base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'> = {
    ruleCode: def.code,
    label: def.label,
    severity: effectiveSeverity,
    sourceModule: def.sourceModule,
  };

  try {
    switch (def.code) {
      case 'R1': return await evaluateR1(base, projectId, context, overrides?.conditionValue);
      case 'R2': return await evaluateR2(base, projectId, context);
      case 'R3': return await evaluateR3(base, projectId, context);
      case 'R4': return await evaluateR4(base, projectId, context);
      case 'R5': return await evaluateR5(base, projectId, context);
      case 'R6': return await evaluateR6(base, projectId, context);
      default:
        return { ...base, passed: true, message: 'Unknown rule' };
    }
  } catch (error) {
    logger.error(`[evaluateRulesImperative] Error evaluating ${def.code}:`, error);
    return { ...base, passed: true, message: 'Không thể kiểm tra (lỗi hệ thống)' };
  }
}

// Evaluate custom rules (C1, C2...)
async function evaluateCustomRule(
  rule: any,
  projectId: string
): Promise<RuleResult> {
  const base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'> = {
    ruleCode: rule.rule_code,
    label: rule.name,
    severity: (rule.severity === 'block' ? 'block' : rule.severity === 'info' ? 'info' : 'warn') as 'block' | 'warn' | 'info',
    sourceModule: rule.source_module,
  };

  try {
    switch (rule.condition_type) {
      case 'gate_minimum': {
        const gateOrder = ['G0', 'G1', 'G2', 'G3', 'G4', 'G5'];
        const threshold = rule.condition_value || 'G3';
        const thresholdIdx = gateOrder.indexOf(threshold);
        const belowGates = gateOrder.slice(0, thresholdIdx);
        if (belowGates.length === 0) return { ...base, passed: true, message: 'Đạt yêu cầu' };
        const { count } = await supabase
          .from('drawings')
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .in('gate', belowGates);
        const passed = (count ?? 0) === 0;
        return { ...base, passed, message: passed ? 'Đạt yêu cầu' : `${count} drawings chưa đạt ${threshold}` };
      }
      case 'status_equals': {
        const table = rule.source_module === 'specs' ? 'diary_spec_items' : 'drawings';
        const { count } = await supabase
          .from(table as any)
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .neq('status', rule.condition_value || '');
        const passed = (count ?? 0) === 0;
        return { ...base, passed, message: passed ? 'Đạt yêu cầu' : `${count} items chưa ${rule.condition_value}` };
      }
      case 'sign_off_green': {
        const { count } = await supabase
          .from('project_sign_offs' as any)
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .eq('module', rule.source_module)
          .neq('status', 'green');
        const passed = (count ?? 0) === 0;
        return { ...base, passed, message: passed ? 'Đạt yêu cầu' : `${count} sign-offs chưa green` };
      }
      case 'count_zero': {
        const table = rule.source_module === 'qaqc' ? 'project_qaqc_items' : 'diary_spec_items';
        const { count } = await supabase
          .from(table as any)
          .select('id', { count: 'exact', head: true })
          .eq('project_id', projectId)
          .eq('status', rule.condition_value || '');
        const passed = (count ?? 0) === 0;
        return { ...base, passed, message: passed ? 'Đạt yêu cầu' : `Còn ${count} items ${rule.condition_value}` };
      }
      default:
        return { ...base, passed: true, message: 'Unknown condition type' };
    }
  } catch (error) {
    logger.error(`[evaluateCustomRule] Error evaluating ${rule.rule_code}:`, error);
    return { ...base, passed: true, message: 'Không thể kiểm tra (lỗi hệ thống)' };
  }
}

// R1: Drawing Zone >= G3 (supports custom gate threshold)
async function evaluateR1(
  base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'>,
  projectId: string,
  context: RuleActionContext,
  customGate?: string
): Promise<RuleResult> {
  if (!context.zone) return { ...base, passed: true, message: 'Không có zone' };

  const gateOrder = ['G0', 'G1', 'G2', 'G3', 'G4', 'G5'];
  const requiredGate = customGate || 'G3';

  const { data: drawings } = await supabase
    .from('drawings')
    .select('gate, zone_id')
    .eq('project_id', projectId);

  const { data: zones } = await supabase
    .from('drawing_zones')
    .select('id, zone_code')
    .eq('zone_code', context.zone);

  const zoneIds = (zones || []).map(z => z.id);
  const zoneDrawings = (drawings || []).filter(d => d.zone_id && zoneIds.includes(d.zone_id));

  if (zoneDrawings.length === 0) {
    return { ...base, passed: true, message: 'Không có bản vẽ trong zone' };
  }

  const minGate = zoneDrawings.reduce((min, d) => {
    const gate = d.gate || 'G0';
    const idx = gateOrder.indexOf(gate);
    const minIdx = gateOrder.indexOf(min);
    return idx < minIdx ? gate : min;
  }, 'G5');

  const minIdx = gateOrder.indexOf(minGate);
  const passed = minIdx >= gateOrder.indexOf(requiredGate);

  return {
    ...base,
    passed,
    message: passed
      ? `Bản vẽ Zone ${context.zone} đạt ${minGate}`
      : `Bản vẽ Zone ${context.zone} chưa duyệt (hiện tại: ${minGate}, yêu cầu: ${requiredGate})`,
    currentValue: minGate,
    linkPath: `/projects/${projectId}?tab=drawings`,
  };
}

// R2: All specs in zone = Approved
async function evaluateR2(
  base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'>,
  projectId: string,
  context: RuleActionContext
): Promise<RuleResult> {
  if (!context.zone) return { ...base, passed: true, message: 'Không có zone' };

  const { data: specs } = await supabase
    .from('diary_spec_items')
    .select('id, status, area')
    .eq('project_id', projectId)
    .eq('area', context.zone)
    .is('deleted_at', null);

  if (!specs || specs.length === 0) {
    return { ...base, passed: true, message: 'Không có specs trong zone' };
  }

  const unapproved = specs.filter(s => s.status !== 'Approved');
  const passed = unapproved.length === 0;

  return {
    ...base,
    passed,
    message: passed
      ? `Tất cả ${specs.length} specs Zone ${context.zone} đã Approved`
      : `Còn ${unapproved.length} specs Zone ${context.zone} chưa Approved`,
    currentValue: `${specs.length - unapproved.length}/${specs.length}`,
    linkPath: `/projects/${projectId}?tab=specs`,
  };
}

// R3: Method Statement >= Submitted
async function evaluateR3(
  base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'>,
  projectId: string,
  context: RuleActionContext
): Promise<RuleResult> {
  const { data: permits } = await supabase
    .from('permits' as any)
    .select('id, permit_type, status')
    .eq('project_id', projectId)
    .eq('permit_type', 'method_statement');

  if (!permits || permits.length === 0) {
    return { ...base, passed: true, message: 'Không có Method Statement' };
  }

  const validStatuses = ['submitted', 'approved', 'Submitted', 'Approved'];
  const unsubmitted = (permits as any[]).filter(
    (p: any) => !validStatuses.includes(p.status)
  );
  const passed = unsubmitted.length === 0;

  return {
    ...base,
    passed,
    message: passed
      ? 'Method Statement đã submitted'
      : `Method Statement cho "${context.entityLabel || 'hạng mục'}" chưa submit`,
    linkPath: `/projects/${projectId}?tab=permit`,
  };
}

// R4: Subcontractor Evaluation exists before payment
async function evaluateR4(
  base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'>,
  projectId: string,
  context: RuleActionContext
): Promise<RuleResult> {
  const { data: evaluations } = await supabase
    .from('project_sign_offs' as any)
    .select('id, sign_off_type, status')
    .eq('project_id', projectId)
    .eq('sign_off_type', 'subcontractor_evaluation');

  const hasEvaluation = evaluations && evaluations.length > 0 &&
    (evaluations as any[]).some((e: any) => e.status === 'signed');

  return {
    ...base,
    passed: !!hasEvaluation,
    message: hasEvaluation
      ? 'Đã có đánh giá nhà thầu'
      : `Chưa đánh giá nhà thầu "${context.entityLabel || ''}"`,
    linkPath: `/projects/${projectId}?tab=closing`,
  };
}

// R5: Spec = Approved before PO creation (warn only)
async function evaluateR5(
  base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'>,
  projectId: string,
  context: RuleActionContext
): Promise<RuleResult> {
  if (!context.entityId) {
    return { ...base, passed: true, message: 'Không có spec liên kết' };
  }

  const { data: spec } = await supabase
    .from('diary_spec_items')
    .select('id, status, code')
    .eq('id', context.entityId)
    .single();

  const passed = spec?.status === 'Approved';

  return {
    ...base,
    passed,
    message: passed
      ? `Spec ${spec?.code || ''} đã Approved`
      : `Spec "${context.entityLabel || spec?.code || ''}" chưa Approved`,
    linkPath: `/projects/${projectId}?tab=specs`,
  };
}

// R6: Sign-off milestone = green before 100%
async function evaluateR6(
  base: Omit<RuleResult, 'passed' | 'message' | 'currentValue' | 'linkPath'>,
  projectId: string,
  context: RuleActionContext
): Promise<RuleResult> {
  if (!context.entityId) {
    return { ...base, passed: true, message: 'Không có milestone' };
  }

  const { data: signOffs } = await supabase
    .from('project_sign_offs' as any)
    .select('id, status, entity_id')
    .eq('project_id', projectId)
    .eq('entity_id', context.entityId)
    .eq('sign_off_type', 'milestone');

  const hasSigned = signOffs && signOffs.length > 0 &&
    (signOffs as any[]).some((s: any) => s.status === 'signed');

  return {
    ...base,
    passed: !!hasSigned,
    message: hasSigned
      ? 'Milestone đã ký xác nhận'
      : `Chưa ký xác nhận milestone "${context.entityLabel || ''}"`,
    linkPath: `/projects/${projectId}?tab=plan`,
  };
}
